import { useState, useEffect, useMemo, useCallback } from 'react';
import { MessageSquare, Menu, History, Trash2, Type, Pencil, Check, X, Pin, PinOff, Paintbrush } from 'lucide-react';
import { useChats } from '@/hooks/useChats';
import { useMsal } from '@azure/msal-react';
import { handleError, ErrorUIStrategy } from '@/errors';
import { CompanyLogo } from './CompanyLogo';
import { ThemeToggle } from './ThemeToggle';
import { chatConfig } from '@/config';
import type { Chat } from '@/services/api';
import { MAX_CHAT_NAME_LENGTH, getChatNameValidationMessage, getSanitizedChatName } from '@/utils/chatValidation';
import sideBarIcon from '@/assets/Sidebar-Icon.png';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';

// Check if error testing is enabled
const USE_ERROR_TESTING = import.meta.env.VITE_USE_ERROR_TESTING === 'true';

interface SidebarProps {
  readonly isOpen: boolean;
  readonly onToggle: () => void;
  readonly currentChatId: number | null;
  readonly onNewChat: () => void;
  readonly onChatSelect: (chatId: number) => void;
  readonly sideBarReload: boolean;
  readonly onInpainting: () => void;
}

export function Sidebar({ isOpen, onToggle, currentChatId, onNewChat, onChatSelect, sideBarReload, onInpainting }: SidebarProps) {
  // Font toggle state: always initialize to Dyslexic font
  const [isDyslexicFont, setIsDyslexicFont] = useState(() => {
    const stored = localStorage.getItem('isDyslexicFont');
    if (stored === null) {
      return true;
    }
    if (stored === 'true') {
      return true;
    }
    return false;
  });
  // Toggle dyslexic font class on <body>
  const DYSLEXIC_CLASS = 'dyslexic-font';

  const handleFontToggle = () => {
    setIsDyslexicFont(prev => {
      if (prev) {
        return false;
      } else {
        return true;
      }
    });
  };
  // Toggle dyslexic font class on <body> and persist to localStorage
  useEffect(() => {
    const body = document.body;
    if (isDyslexicFont) {
      body.classList.add(DYSLEXIC_CLASS);
    } else {
      body.classList.remove(DYSLEXIC_CLASS);
    }
    localStorage.setItem('isDyslexicFont', String(isDyslexicFont));
  }, [isDyslexicFont]);
  const { getChats, renameChat, deleteChat, pinChat } = useChats();
  const { accounts } = useMsal();
  const [chats, setChats] = useState<Chat[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatToDelete, setChatToDelete] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingChatId, setEditingChatId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [pendingPinIds, setPendingPinIds] = useState<Set<number>>(new Set());

  const [loadChatHistory, setLoadChatHistory] = useState<boolean>(USE_ERROR_TESTING);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Get username from MSAL account - memoized to avoid recalculation on every render
  const username = useMemo(() => {
    let name = accounts.length > 0 ? accounts[0].name || accounts[0].username : 'User';
    if (name.includes('(')) {
      name = name.substring(0, name.indexOf('(')).trim();
    }
    return name;
  }, [accounts]);

  const sortByUpdatedAtDesc = useCallback((chatList: Chat[]) => {
    return [...chatList].sort((firstChat, secondChat) => {
      return new Date(secondChat.updatedAt).getTime() - new Date(firstChat.updatedAt).getTime();
    });
  }, []);

  const setSortedChats = useCallback((nextChats: Chat[] | ((previousChats: Chat[]) => Chat[])) => {
    setChats((previousChats) => {
      const updatedChats = typeof nextChats === 'function' ? nextChats(previousChats) : nextChats;
      return sortByUpdatedAtDesc(updatedChats);
    });
  }, [sortByUpdatedAtDesc]);

  // Fetch chats when component mounts or when sidebar opens
  const fetchChats = useCallback(async (page: number = 1, append: boolean = false) => {
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      setError(null);
      
      const fetchedChats = await getChats(page, chatConfig.pageSize);
      
      setSortedChats((previousChats) => append ? [...previousChats, ...fetchedChats] : fetchedChats);
      
      // Show "Show more" only if we received a full page
      // If we get fewer chats than requested, we've reached the end
      // Note: If total chats is exactly a multiple of pageSize, the button will appear
      // on the last page but will load an empty array and then disappear
      setHasMoreChats(fetchedChats.length === chatConfig.pageSize);
    } catch (err) {
      await handleError(err, {
        setError,
        context: 'Loading chats',
        strategy: ErrorUIStrategy.SIDEBAR
      });
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [getChats, setSortedChats]);

  useEffect(() => {
    if (isOpen && loadChatHistory) {
      setCurrentPage(1);
      setHasMoreChats(true);
      fetchChats(1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sideBarReload, loadChatHistory]);

  const handleNewChat = () => {
    onNewChat();
    if (window.innerWidth < 768) {
      onToggle(); // Close sidebar on mobile after action
    }
  };

  const handleHistoryReload = () => {
    const newLoadChatHistory = !loadChatHistory;
    setLoadChatHistory(newLoadChatHistory);

    setChats([]);

    if (newLoadChatHistory) {
      // When enabling chat history, reset pagination
      // The useEffect will handle fetching when loadChatHistory changes
      setCurrentPage(1);
      setHasMoreChats(true);
    }
  };

  const loadMoreChats = async () => {
    if (!hasMoreChats || isLoadingMore) return;

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchChats(nextPage, true);
  };

  const handleChatSelect = (chatId: number) => {
    if (isModalOpen) return; // Prevent changing chat when modal is open
    onChatSelect(chatId);
    if (window.innerWidth < 768) onToggle();
  };

  const handleRenameStart = (chat: Chat) => {
    setEditingChatId(chat.id);
    setEditingName(chat.name);
  };

  const handleRenameCancel = () => {
    if (isRenaming) {
      return;
    }

    setEditingChatId(null);
    setEditingName('');
  };

  const handleRenameSubmit = async (chat: Chat) => {
    const trimmedName = getSanitizedChatName(editingName);
    const validationMessage = getChatNameValidationMessage(editingName);

    if (validationMessage !== null) {
      return;
    }

    if (trimmedName === chat.name) {
      handleRenameCancel();
      return;
    }

    try {
      setIsRenaming(true);
      await renameChat(chat.id, trimmedName);
      setSortedChats((previousChats) => previousChats.map((currentChat) => {
        if (currentChat.id !== chat.id) {
          return currentChat;
        }

        return {
          ...currentChat,
          name: trimmedName,
          updatedAt: new Date().toISOString()
        };
      }));
      setEditingChatId(null);
      setEditingName('');
    } catch (err) {
      setEditingChatId(null);
      setEditingName('');
      await handleError(err, {
        setError: setError,
        context: 'Renaming chat',
        strategy: ErrorUIStrategy.SIDEBAR
      });
    } finally {
      setIsRenaming(false);
    }
  };

  // Trash clicked → open delete modal
  const handleDeleteClick = (chatId: number) => {
    if (editingChatId === chatId) {
      handleRenameCancel();
    }
    setChatToDelete(chatId);
    setIsModalOpen(true);
     
  };

  // Confirm deletion
  const confirmDelete = async () => {
    if (chatToDelete === null) return;

    try {
      await deleteChat(chatToDelete);

      const wasCurrentChat = chatToDelete === currentChatId;

      // Calculate the updated chats list first
      const updatedChats = chats.filter((c) => c.id !== chatToDelete);
      
      setSortedChats(updatedChats);

      // Handle navigation after successful deletion
      if (wasCurrentChat) {
        if (updatedChats.length > 0) {
          // Find the index of the deleted chat in the original array
          const deletedIndex = chats.findIndex((c) => c.id === chatToDelete);
          // Move to next chat if exists, else previous chat
          const nextChat = updatedChats[deletedIndex] || updatedChats[deletedIndex - 1];
          if (nextChat) {
            onChatSelect(nextChat.id);
          }
        } else {
          // Last chat deleted → start a new chat
          onNewChat();
        }
      }
    } catch (err) {
      console.error('Failed to delete chat:', err);
    
    } finally {
      setIsModalOpen(false);
      setChatToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsModalOpen(false);
    setChatToDelete(null);
  };

  // Handle pin/unpin toggle
  const handlePinToggle = async (chat: Chat) => {
    const newPinnedState = !chat.isPinned;
    
    // Optimistic update
    setSortedChats((previousChats) =>
      previousChats.map((c) =>
        c.id === chat.id ? { ...c, isPinned: newPinnedState } : c
      )
    );

    setPendingPinIds((prev) => new Set(prev).add(chat.id));

    try {
      const updatedChat = await pinChat(chat.id, newPinnedState);
      setSortedChats((previousChats) =>
        previousChats.map((currentChat) =>
          currentChat.id === chat.id ? updatedChat : currentChat
        )
      );
    } catch (err) {
      // Rollback on error
      setSortedChats((previousChats) =>
        previousChats.map((c) =>
          c.id === chat.id ? { ...c, isPinned: chat.isPinned } : c
        )
      );

      await handleError(err, {
        setError,
        context: 'Updating chat pin state',
        strategy: ErrorUIStrategy.SIDEBAR
      });
    } finally {
      setPendingPinIds((prev) => {
        const next = new Set(prev);
        next.delete(chat.id);
        return next;
      });
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };

  // Keyboard handler for the rename input
  const handleRenameKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, chat: Chat) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void handleRenameSubmit(chat);
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      handleRenameCancel();
    }
  };

  // Renders the inline rename form for a chat item
  const renderChatEditingForm = useCallback((chat: Chat) => {
    const renameValidationMessage = getChatNameValidationMessage(editingName);
    const inputClassName = `w-full rounded-md border px-3 py-2 text-sm text-gray-900 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 ${renameValidationMessage === null ? 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 bg-white' : 'border-red-500 bg-red-50 dark:border-red-500 dark:bg-red-950/20 focus:ring-red-500'}`;
    const counterClassName = `text-xs truncate ${renameValidationMessage === null ? 'text-gray-500 dark:text-gray-400' : 'text-red-600 dark:text-red-400'}`;

    return (
      <div className="px-3 py-3">
        <div className="flex items-start space-x-3">
          <MessageSquare className="w-4 h-4 mt-2 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={editingName}
              onChange={(event) => { setEditingName(event.target.value); }}
              onKeyDown={(event) => handleRenameKeyDown(event, chat)}
              autoFocus
              className={inputClassName}
              aria-label={`Rename chat: ${chat.name}`}
              disabled={isRenaming}
            />
            {renameValidationMessage !== null && (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                {renameValidationMessage}
              </p>
            )}
            <div className="mt-2 flex items-center justify-between gap-2">
              <p className={counterClassName}>
                {getSanitizedChatName(editingName).length}/{MAX_CHAT_NAME_LENGTH}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => void handleRenameSubmit(chat)}
                  className="p-1 text-green-600 hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 rounded disabled:opacity-50"
                  aria-label={`Save chat name for ${chat.name}`}
                  disabled={isRenaming || renameValidationMessage !== null}
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={handleRenameCancel}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 rounded disabled:opacity-50"
                  aria-label={`Cancel renaming chat: ${chat.name}`}
                  disabled={isRenaming}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [editingName, isRenaming, handleRenameKeyDown, handleRenameSubmit, handleRenameCancel]);

  // Renders the read-only view of a chat item with action buttons
  const renderChatViewMode = useCallback((chat: Chat) => {
    const chatNameClassName = `text-sm font-medium truncate${isDyslexicFont ? ' dyslexic-chat-name' : ''}`;
    const pinAriaLabel = chat.isPinned ? `Unpin chat: ${chat.name}` : `Pin chat: ${chat.name}`;
    const pinTitle = chat.isPinned ? 'Unpin' : 'Pin';
    const PinIcon = chat.isPinned ? PinOff : Pin;
    const pinButtonClassName = `p-1 rounded focus:outline-none focus:ring-2 transition-all disabled:opacity-50 ${
      chat.isPinned
        ? 'text-amber-600 bg-amber-100 hover:bg-amber-200 focus:ring-amber-400 dark:text-amber-300 dark:bg-amber-900/40 dark:hover:bg-amber-900/60 dark:focus:ring-amber-500'
        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200 focus:ring-gray-400 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-800'
    }`;

    return (
      <>
        <button
          onClick={() => handleChatSelect(chat.id)}
          aria-label={`Select chat: ${chat.name}`}
          className="w-full text-left px-3 py-2 rounded-lg border-0 bg-transparent cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <div className="flex items-start space-x-3">
            <MessageSquare className="w-4 h-4 mt-1 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className={chatNameClassName} title={chat.name}>
                {chat.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 pr-20">
                {formatDate(chat.createdAt)}
              </p>
            </div>
          </div>
        </button>

        <div className="absolute bottom-2 right-2 flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 transition-opacity">
          <button
            onClick={(event) => { event.stopPropagation(); void handlePinToggle(chat); }}
            className={pinButtonClassName}
            aria-label={pinAriaLabel}
            disabled={pendingPinIds.has(chat.id)}
            title={pinTitle}
          >
            <PinIcon className="w-3 h-3" />
          </button>
          <button
            onClick={(event) => { event.stopPropagation(); handleRenameStart(chat); }}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 rounded"
            aria-label={`Rename chat: ${chat.name}`}
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            onClick={(event) => { event.stopPropagation(); handleDeleteClick(chat.id); }}
            className="p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 rounded"
            aria-label={`Delete chat: ${chat.name}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </>
    );
  }, [isDyslexicFont, pendingPinIds, formatDate, handleChatSelect, handlePinToggle, handleRenameStart, handleDeleteClick]);

  // Render individual chat item
  const renderChatItem = useCallback((chat: Chat) => {
    const isEditing = editingChatId === chat.id;
    return (
      <div
        key={chat.id}
        className={`
          w-full rounded-lg transition-colors group relative
          ${
            currentChatId === chat.id
              ? 'bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-white'
              : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
          }
        `}
      >
        {isEditing ? renderChatEditingForm(chat) : renderChatViewMode(chat)}
      </div>
    );
  }, [editingChatId, currentChatId, renderChatEditingForm, renderChatViewMode]);
const chatToDeleteName = useMemo(
  () =>
    isModalOpen && chatToDelete !== null
      ? chats.find((c) => c.id === chatToDelete)?.name
      : undefined,
  [isModalOpen, chatToDelete, chats]
);

// Split chats into pinned and recent
const { pinnedChats, recentChats } = useMemo(() => {
  const pinned = chats.filter(c => c.isPinned);
  const recent = chats.filter(c => !c.isPinned);
  return { pinnedChats: pinned, recentChats: recent };
}, [chats]);
  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && !isModalOpen && (
        <button
          type="button"
          aria-label="Close sidebar"
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden cursor-default border-0 p-0"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      {isOpen === false && (
        <div className="flex flex-col items-start px-4 py-4 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md shadow-md w-fit h-fit">
          {/* Icon in top-left */}
          <button
            onClick={onToggle}
            className="p-0 border-0 bg-transparent"
            aria-label="Open sidebar"
          >
            <img
              src={sideBarIcon}
              alt="Company Logo"
              className="w-4 sm:w-5 md:w-6 lg:w-6 xl:w-6 h-auto object-contain cursor-pointer brightness-[0.3] dark:invert"
            />
          </button>
        </div>
      )}
      <div className={`
        fixed top-0 left-0 h-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white z-50 transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        w-80 md:w-64 flex flex-col border border-gray-300 dark:border-black border-b-0 text-sm
      `}>
        {/* Header */}
        <div className="flex flex-col items-end px-4 py-4 rounded-md shadow-md">
          {/* Icon in top-left */}
          <button
            onClick={onToggle}
            className="p-0 border-0 bg-transparent"
            aria-label="Close sidebar"
          >
            <img
              src={sideBarIcon}
              alt="Company Logo"
              className="w-4 sm:w-5 md:w-6 lg:w-6 xl:w-6 h-auto object-contain cursor-pointer brightness-[0.3] dark:invert"
            />
          </button>
        </div>

        {/* New Chat Button */}
        <div className="p-4">
          <button
            onClick={handleNewChat}
            className="w-full flex items-center space-x-3 px-4 py-3 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 rounded-full transition-colors cursor-pointer"
          >
            <MessageSquare className="w-6 h-6 text-gray-700 dark:text-white" />
            <span>New Chat</span>
          </button>
        </div>
           {/* Inpainting Button  */}
        <div className="px-4 pb-3">
          <button
            onClick={onInpainting}
            className="w-full flex items-center space-x-3 px-4 py-3 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 rounded-full transition-colors cursor-pointer"
            aria-label="Open inpainting for a local image"
          >
            <Paintbrush className="w-6 h-6 text-gray-700 dark:text-white" />
            <span>Inpainting</span>
          </button>
        </div>
        <div className="px-4 pt-1">
          <button
            onClick={handleHistoryReload}
            className="w-full flex items-center space-x-3 px-4 py-3 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 rounded-full transition-colors cursor-pointer"
          >
            <History className="w-6 h-6 text-gray-700 dark:text-white" />
            <span>History</span>
          </button>
        </div>

        {/* Chat History */}
        {
          loadChatHistory === true && (
            <div className="h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-rounded flex-1 px-4">
              <div className="space-y-2">
                {/* Favorites Section */}
                {!isLoading && !error && pinnedChats.length > 0 && (
                  <>
                    <h3
                      className="text-sm font-medium text-gray-600 dark:text-gray-400 px-2 mb-3 mt-4"
                      title="Your pinned chats (favorites)"
                    >
                      Favorites
                    </h3>
                    {pinnedChats.map((chat) => renderChatItem(chat))}
                  </>
                )}

                {/* Recent Chats Section */}
                <h3
                  className="text-sm font-medium text-gray-600 dark:text-gray-400 px-2 mb-3 mt-4"
                  title="This section shows your recent chat history"
                >
                  Recent Chats
                </h3>

                {/* Loading State */}
                {isLoading && (
                  <div className="px-3 py-2 text-center text-gray-500 dark:text-gray-400">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="text-xs mt-2">Loading chats...</p>
                  </div>
                )}

                {/* Error State */}
                {error && (
                  <div className="px-3 py-2 text-center text-red-500 dark:text-red-400">
                    <p className="text-xs">Error while loading chat history</p>
                    <button
                      onClick={() => fetchChats(1, false)}
                      className="text-xs underline hover:no-underline mt-1"
                    >
                      Try again
                    </button>
                  </div>
                )}

                {/* Chat List */}
                {!isLoading && !error && chats.length === 0 && (
                  <div className="px-3 py-2 text-center text-gray-500 dark:text-gray-400">
                    <p className="text-xs">No chats yet</p>
                    <p className="text-xs mt-1">Start a conversation!</p>
                  </div>
                )}

                {!isLoading && !error && recentChats.map((chat) => renderChatItem(chat))}

                {/* Show More Button */}
                {!isLoading && !error && hasMoreChats && chats.length > 0 && (
                  <div className="px-3 py-4">
                    <button
                      onClick={loadMoreChats}
                      disabled={isLoadingMore}
                      className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoadingMore ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 dark:border-gray-300"></div>
                          <span>Loading...</span>
                        </div>
                      ) : (
                        'Show more'
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )
        }

          {/* Render the ConfirmDeleteModal once (outside the .map) */}
        {isModalOpen && chatToDelete !== null && (
          <ConfirmDeleteModal
            message={
              chatToDeleteName
                ? `Are you sure you want to delete "${chatToDeleteName}"?`
                : 'Are you sure you want to delete this chat?'
            }
            onConfirm={confirmDelete}
            onCancel={cancelDelete}
          />
        )}
        
        {/* User Section */}
        <div className={`w-full p-4 border-gray-300 dark:border-gray-700 bottom-0 ${loadChatHistory === true ? 'sticky' : 'fixed'}`}>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <CompanyLogo size={32} />
              <div className="flex-1 min-w-0">
                <span
                  className={`text-sm font-medium truncate text-gray-900 dark:text-white block${isDyslexicFont ? ' dyslexic-chat-name' : ''}`}
                >
                  {username}
                </span>
              </div>
            </div>
            <div className="flex-shrink-0 ml-3 flex items-center space-x-2">
              <ThemeToggle />
              <button
                onClick={handleFontToggle}
                className="p-2 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 rounded-full transition-colors"
                title={isDyslexicFont ? 'Switch to Default Font' : 'Switch to Dyslexic Font'}
                aria-label="Toggle font family"
              >
                <Type className="w-5 h-5 text-gray-700 dark:text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toggle Button (when sidebar is closed) */}
      {!isOpen && (
        <button
          onClick={onToggle}
          className="fixed top-4 left-4 z-30 p-2 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-700 dark:text-white rounded-md transition-colors md:hidden"
        >
          <Menu className="w-5 h-5 text-gray-700 dark:text-white" />
        </button>
      )}
    </>
  );
}
